﻿import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, FileText, BookOpen, User, BookMarked, X } from 'lucide-react';
import { db } from '../db/db';

interface GlobalSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface SearchResult {
  id: string;
  type: 'plan' | 'video' | 'note';
  title: string;
  subtitle?: string;
  url: string;
  icon: React.ElementType;
}

const GlobalSearchModal: React.FC<GlobalSearchModalProps> = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 50);
      setQuery('');
      setResults([]);
      setSelectedIndex(0);
    }
  }, [isOpen]);

  useEffect(() => {
    const performSearch = async () => {
      if (!query.trim() || query.length < 2) {
        setResults([]);
        return;
      }

      const lowerQuery = query.toLowerCase();
      const newResults: SearchResult[] = [];

      try {
        // 1. Search Subjects/Plans
        const subjects = await db.subjects.toArray();
        const matchedSubjects = subjects.filter(s => s.name.toLowerCase().includes(lowerQuery));
        
        for (const subject of matchedSubjects) {
          const plans = await db.studyPlans.where('subjectId').equals(subject.id!).toArray();
          plans.forEach(plan => {
            newResults.push({
              id: `plan-${plan.id}`,
              type: 'plan',
              title: `${subject.name} Planı`,
              subtitle: 'Çalışma Planı',
              url: `/plan/${plan.id}`,
              icon: BookOpen
            });
          });
        }

        // 2. Search Videos and Notes
        const items = await db.scheduleItems.toArray();
        const matchedItems = items.filter(item => 
          item.videoTitle.toLowerCase().includes(lowerQuery) || 
          (item.teacherName && item.teacherName.toLowerCase().includes(lowerQuery)) ||
          (item.notes && item.notes.toLowerCase().includes(lowerQuery))
        );

        // Limit results to prevent lag
        const topItems = matchedItems.slice(0, 15);

        topItems.forEach(item => {
          if (item.notes && item.notes.toLowerCase().includes(lowerQuery)) {
            newResults.push({
              id: `note-${item.id}`,
              type: 'note',
              title: `Not: ${item.videoTitle}`,
              subtitle: item.notes.substring(0, 60) + '...',
              url: `/plan/${item.studyPlanId}`,
              icon: BookMarked
            });
          } else {
            newResults.push({
              id: `video-${item.id}`,
              type: 'video',
              title: item.videoTitle,
              subtitle: item.teacherName ? `Öğretmen: ${item.teacherName}` : 'Video',
              url: `/plan/${item.studyPlanId}`,
              icon: item.teacherName && item.teacherName.toLowerCase().includes(lowerQuery) ? User : FileText
            });
          }
        });

        setResults(newResults);
        setSelectedIndex(0);
      } catch (error) {
        console.error("Search error:", error);
      }
    };

    const debounceTimer = setTimeout(performSearch, 200);
    return () => clearTimeout(debounceTimer);
  }, [query]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => (prev < results.length - 1 ? prev + 1 : prev));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : prev));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (results[selectedIndex]) {
          handleSelect(results[selectedIndex]);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, results, selectedIndex, onClose]);

  const handleSelect = (result: SearchResult) => {
    navigate(result.url);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh] px-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
      {/* Click away listener */}
      <div className="absolute inset-0" onClick={onClose}></div>
      
      <div className="relative w-full max-w-2xl bg-white dark:bg-slate-900 rounded-3xl shadow-2xl shadow-slate-900/50 overflow-hidden border border-border flex flex-col animate-in slide-in-from-top-4 duration-300">
        <div className="flex items-center px-6 py-4 border-b border-border">
          <Search size={24} className="text-accent-500 mr-4 shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Plan, video veya not ara..."
            className="flex-1 bg-transparent border-none outline-none text-xl font-medium text-slate-800 dark:text-slate-200 placeholder:text-slate-400"
          />
          <button 
            onClick={onClose}
            title="Aramayı Kapat"
            aria-label="Aramayı Kapat"
            className="p-1.5 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 bg-slate-100 dark:bg-slate-800/80 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors ml-4"
          >
            <X size={20} />
          </button>
        </div>

        {results.length > 0 && (
          <div className="max-h-[60vh] overflow-y-auto p-3">
            {results.map((result, idx) => {
              const Icon = result.icon;
              return (
                <div
                  key={`${result.id}-${idx}`}
                  onClick={() => handleSelect(result)}
                  className={`flex items-center gap-4 px-4 py-3 rounded-xl cursor-pointer transition-all ${
                    idx === selectedIndex 
                      ? 'bg-accent-100 dark:bg-accent-500/10 border border-accent-500/20' 
                      : 'border border-transparent hover:bg-slate-100 dark:hover:bg-slate-800/50'
                  }`}
                >
                  <div className={`p-2.5 rounded-lg shrink-0 ${
                    idx === selectedIndex 
                      ? 'bg-accent-100 dark:bg-accent-500/10 text-accent-600 dark:text-accent-500' 
                      : 'bg-slate-100 dark:bg-slate-800/80 text-slate-500 dark:text-slate-400'
                  }`}>
                    <Icon size={20} />
                  </div>
                  <div className="flex-1 overflow-hidden">
                    <h4 className={`text-sm font-bold truncate ${
                      idx === selectedIndex ? 'text-accent-600 dark:text-accent-500' : 'text-slate-700 dark:text-slate-200'
                    }`}>
                      {result.title}
                    </h4>
                    {result.subtitle && (
                      <p className={`text-xs truncate mt-0.5 ${
                        idx === selectedIndex ? 'text-accent-500/80 dark:text-accent-500/80' : 'text-slate-500 dark:text-slate-400'
                      }`}>
                        {result.subtitle}
                      </p>
                    )}
                  </div>
                  {idx === selectedIndex && (
                    <span className="text-[10px] font-black uppercase tracking-widest text-accent-500 bg-accent-100 dark:bg-accent-500/10 px-2 py-1 rounded">
                      GİT ↵
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {query.length >= 2 && results.length === 0 && (
          <div className="p-12 flex flex-col items-center justify-center text-center">
            <Search size={48} className="text-slate-300 dark:text-slate-700 mb-4" />
            <h3 className="text-lg font-bold text-slate-800 dark:text-slate-200 mb-1">Sonuç Bulunamadı</h3>
            <p className="text-sm text-slate-500">"{query}" için bir eşleşme bulamadık.</p>
          </div>
        )}

        <div className="px-6 py-3 border-t border-border bg-slate-100 dark:bg-slate-800/50 flex justify-between items-center text-[10px] font-bold text-slate-400 uppercase tracking-widest">
          <div className="flex gap-4">
            <span className="flex items-center gap-1"><kbd className="px-1.5 py-0.5 rounded bg-white dark:bg-slate-700 border border-slate-400 dark:border-slate-600">↑↓</kbd> Gezin</span>
            <span className="flex items-center gap-1"><kbd className="px-1.5 py-0.5 rounded bg-white dark:bg-slate-700 border border-slate-400 dark:border-slate-600">↵</kbd> Seç</span>
          </div>
          <span className="flex items-center gap-1"><kbd className="px-1.5 py-0.5 rounded bg-white dark:bg-slate-700 border border-slate-400 dark:border-slate-600">ESC</kbd> Kapat</span>
        </div>
      </div>
    </div>
  );
};

export default GlobalSearchModal;






